import { inject } from '@angular/core';
import { getLanguage, getSelectedExhibitionId, getSelfRegistration } from '@app/app.reducer';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { OrderPreparationService } from '@core/data-access/services/order-preparation.service';
import { PrepareOrderActions } from '@core/data-access/store/prepare-order/prepare-order.actions';
import { CreateSavedTicketHolders } from '@core/features/account/features/saved-ticket-holders/services/create-saved-ticket-holders';
import { SavedTicketHoldersApi } from '@core/features/account/features/saved-ticket-holders/services/saved-ticket-holders-api';
import { SavedTicketHolderActions } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.actions';
import { mapCreatedTicketHoldersResponseToSetSavedTicketHolders } from '@core/features/account/features/saved-ticket-holders/utils/saved-ticket-holder-mappers';
import { selectBuyerInformationEmailAddress } from '@core/features/buyer-information/store/buyer-information.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BookingActions } from '@shared/services-with-reducers/products/booking/booking.actions';
import { shouldSendAnonymousProductToAPI } from '@shared/services-with-reducers/products/booking/booking.selectors';
import { SendOrder } from '@store/step-forms/steps-forms.actions';
import { every, isEmpty } from 'lodash';
import { catchError, filter, iif, map, of, switchMap, tap } from 'rxjs';

export const skipOrCreateTicketHolderPersonsBeforeSaveOrder$ = createEffect(
  (actions$ = inject(Actions), createSavedTicketHolders = inject(CreateSavedTicketHolders)) =>
    actions$.pipe(
      ofType(PrepareOrderActions.prepareAndSendOrder),
      switchMap(() =>
        createSavedTicketHolders
          .prepareUnsavedPersonsForCreateSavedTicketHoldersRequest$()
          .pipe(
            switchMap((createSavedTicketHoldersRequest) =>
              iif(
                () => isEmpty(createSavedTicketHoldersRequest),
                of(PrepareOrderActions.skipCreateTicketHolderPersonsBeforeSaveOrder()),
                of(PrepareOrderActions.createTicketHolderPersonsBeforeSaveOrder(createSavedTicketHoldersRequest))
              )
            )
          )
      )
    ),
  { functional: true }
);

export const setCreatedSavedTicketHolderPersonsBeforeSaveOrder$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    translateService = inject(TranslateService),
    dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory),
    savedTicketHoldersApi = inject(SavedTicketHoldersApi)
  ) =>
    actions$.pipe(
      ofType(PrepareOrderActions.createTicketHolderPersonsBeforeSaveOrder),
      filter(({ createSavedTicketHoldersRequest }) => !isEmpty(createSavedTicketHoldersRequest)),
      concatLatestFrom(() => [
        store.select(getLanguage),
        store.select(selectBuyerInformationEmailAddress),
        store.select(getSelectedExhibitionId),
        store.select(getSelfRegistration)
      ]),
      switchMap(([{ createSavedTicketHoldersRequest }, language, buyerEmail, eventId, isSelfRegistration]) =>
        savedTicketHoldersApi
          .createSavedTicketHolders$(language, buyerEmail, eventId, isSelfRegistration, createSavedTicketHoldersRequest)
          .pipe(
            map((createSavedTicketHoldersResponse) => {
              const createdTicketHolders = mapCreatedTicketHoldersResponseToSetSavedTicketHolders(
                createSavedTicketHoldersResponse,
                translateService,
                dynamicFieldOptionsTranslationFactory
              );

              const areAllTicketHoldersCreated = every(createSavedTicketHoldersResponse, ({ validationError }) =>
                isEmpty(validationError)
              );

              return SavedTicketHolderActions.setCreatedSavedTicketHolderPersonsBeforeSaveOrder({
                createdTicketHolders,
                areAllTicketHoldersCreated
              });
            }),
            catchError(() =>
              of(
                SavedTicketHolderActions.setCreatedSavedTicketHolderPersonsBeforeSaveOrder({
                  createdTicketHolders: null,
                  areAllTicketHoldersCreated: false
                })
              )
            )
          )
      )
    ),
  { functional: true }
);

export const prepareDataForSaveOrderRequestAfterCreatingSavedTicketHolderPersons$ = createEffect(
  (actions$ = inject(Actions), orderPreparationService = inject(OrderPreparationService)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.setCreatedSavedTicketHolderPersonsBeforeSaveOrder),
      tap(({ areAllTicketHoldersCreated }) => orderPreparationService.setIsOrderProcessing(areAllTicketHoldersCreated)),
      filter(({ areAllTicketHoldersCreated }) => areAllTicketHoldersCreated),
      switchMap(() => orderPreparationService.prepareSaveOrderRequest$()),
      map((orderRequest) => new SendOrder(orderRequest))
    ),
  { functional: true }
);

export const prepareDataForSaveOrderRequestWithoutCreatingSavedTicketHolderPersons$ = createEffect(
  (actions$ = inject(Actions), orderPreparationService = inject(OrderPreparationService)) =>
    actions$.pipe(
      ofType(PrepareOrderActions.skipCreateTicketHolderPersonsBeforeSaveOrder),
      switchMap(() => orderPreparationService.prepareSaveOrderRequest$()),
      map((orderRequest) => new SendOrder(orderRequest))
    ),
  { functional: true }
);

export const prepareDataForAnonymousTicketSaveOrder$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), orderPreparationService = inject(OrderPreparationService)) =>
    actions$.pipe(
      ofType(PrepareOrderActions.prepareAnonymousTicketOrder),
      concatLatestFrom(() => store.select(shouldSendAnonymousProductToAPI)),
      filter(([_, shouldSendAnonymousProductToAPI]) => shouldSendAnonymousProductToAPI),
      tap(() => store.dispatch(BookingActions.setIsAnonymousProductSentToAPI(true))),
      switchMap(() => orderPreparationService.prepareSaveOrderRequest$(true)),
      map((orderRequest) => new SendOrder(orderRequest))
    ),
  { functional: true }
);
